import * as React from "react";
import Api from "./Api";
import "./DynCampusPage.scss";
import LookupCtl from "./input/LookupCtl";
import LabelCtl from "./input/LabelCtl";
import SelectCtl from "./input/SelectCtl";
import TextCtl from "./input/TextCtl";
import PostalAddressCtl from "./PostalAddressCtl"
import { DynAccount } from "./DynAccount";
import { Campus } from "@testout/testout-commerce/models/labsim/Campus";
import { PostalAddress } from "@testout/testout-commerce/models/commerce/PostalAddress";
import { ListItem } from "@testout/testout-commerce/models/ListItem";
import Segment from "@testout/testout-commerce/models/Segment";


interface DynCampusPageProps {
    entityId?: string;
    entityType?: string;
    campusProfileId?: string;
    env?: string;
}

interface DynCampusPageState {
    initialized: boolean;
    inProgress: string;
    errorMessage: string;
    campus: Campus;

    showAssociateDlg: boolean;
    associateCampusProfileId?: string;
}

export default class DynCampusPage extends React.PureComponent<DynCampusPageProps, DynCampusPageState> {
    constructor(props: DynCampusPageProps) {
        super(props);
        this.state = {
            initialized: false, inProgress: "", errorMessage: "",
            campus: { campusDisplayStyle: "", campusName: "" },
            showAssociateDlg: false,
        };
    }

    private _displayStyle: ListItem[] = [
        { name: "School", value: "SCHOOL" },
        { name: "Campus", value: "CAMPUS" },
        { name: "Company", value: "COMPANY" }
    ];

    async componentDidMount() {
        // Parse the URL:
        // campus?entityId=xxxx&entityType=yyyy  - Associate a new or existing campus with D365 entity id xxxx of type yyyy
        // campus/xxxx            - Bring up campus xxxx from LabSim
        // campus                 - Bring up a blank campus page

        if (this.props.entityId && this.props.entityType)
            this.initCampus(true);
            //this.setState({ initialized: true, showAssociateDlg: true, campusDefaults: campusDefaults, associateCampusProfileId: this.props.campusProfileId });
        else if (this.props.campusProfileId)
            this.loadCampus();
        else
            this.initCampus();
    }

    // Initialize the campus fields from account or contact or indirectly though order/line quote/line or default values
    initCampus = async (associate: boolean = false) => {
        try {
            this.setState({ errorMessage: "", inProgress: "Initializing from entity..." });

            let defaultCampus: Campus|null = null;
            if (this.props.entityType && this.props.entityId)
                defaultCampus = await Api.get<Campus>(`/api/campuses/defaults/${this.props.entityType}/${this.props.entityId}?env=${this.props.env ?? ""}`);
            defaultCampus = defaultCampus ?? { campusName: "", campusDisplayStyle: "CAMPUS", country: "USA" };
            let { campusId, ...campus } = defaultCampus;

            if (associate)
                this.setState({ initialized: true, showAssociateDlg: true, campus: campus, inProgress: "", associateCampusProfileId: campusId });
            else
                this.setState({ initialized: true, showAssociateDlg: false, campus: campus, inProgress: "" });
        }
        catch (error: any) {
            this.setState({ initialized: true, errorMessage: `Error while loading the Dynamics entity information: ${error.message}`, inProgress: "" });
        }
    }

    // Load an existing campus from LabSim
    loadCampus = async () => {
        try {
            let campus: Campus = await Api.get(`/api/campuses/${this.props.campusProfileId}`);

            this.setState({ initialized: true, campus: campus });
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while loading the institution: ${error.message}`, initialized: true });
        }
    }

    // Save the current customer to the Commerce Server
    saveCampus = async () => {
        try {
            let campus = this.state.campus;

            // Validate that necessary fields have values
            let msg = "";
            if (!campus.campusName)
                msg += " Please enter a campus name.";
            if (campus.campusId)
                msg += " Updating a CertMaster Institution is not currectly supported."
            if (msg) {
                this.setState({ errorMessage: msg });
                return;
            }

            this.setState({ errorMessage: "", inProgress: `Saving${this.props.entityId ? " and associating" : ""} CertMaster Insitution...` });

            campus = await Api.post<Campus>("/api/campuses", campus);
            if (this.props.entityId && this.props.entityType)
                await Api.put<object>(`/api/campuses/${campus.campusId}/associations/${this.props.entityType}/${this.props.entityId}?env=${this.props.env ?? ""}`, null);

            this.setState({ inProgress: "", campus: campus });
            this.closeWindow(campus.campusId);
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while saving institution: ${error.message}`, inProgress: "" });
        }
    }

    // Associate the campus with an entity in Dynamics
    linkCampus = async () => {
        try {
            if (!this.state.associateCampusProfileId)
                throw new Error("Please select an institution before associating.");

            this.setState({ errorMessage: "", inProgress: "Associating institution..." });

            if (this.props.entityId && this.props.entityType)
                await Api.put<object>(`/api/campuses/${this.state.associateCampusProfileId}/associations/${this.props.entityType}/${this.props.entityId}?env=${this.props.env ?? ""}`, null);

            this.setState({ inProgress: "" });
            this.closeWindow(this.state.associateCampusProfileId);
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while associating institution: ${error.message}`, inProgress: "" });
        }
    }

    onAssociateCampusSearch = async (value: string, exact?: boolean): Promise<ListItem[]> => {
        return exact
            ? [await Api.get<ListItem>(`/api/campuses/${value}/list-item`)]
            : await Api.get<ListItem[]>(`/api/campuses/list-items?search=${value}`);
    }

    onAssociateCampusChange = (value: string) => {
        this.setState({ associateCampusProfileId: value });
    }

    onCampusSelect = async (value: string, exact?: boolean): Promise<ListItem[]> => {
        return exact
            ? [await Api.get<ListItem>(`/api/campuses/${value}/list-item`)]
            : await Api.get<ListItem[]>(`/api/campuses/list-items?search=${value}`);
    }

    onNameChange = (value: string) => {
        let campus = { ...this.state.campus, campusName: value };
        this.setState({ campus: campus });
    }

    onDisplayStyleChange = (value: string) => {
        let campus = { ...this.state.campus, campusDisplayStyle: value };
        this.setState({ campus: campus });
    }

    onPostalChange = (value: PostalAddress) => {
        let campus = {
            ...this.state.campus,
            address: value.line1,
            city: value.city,
            region: value.stateOrProvince,
            postalCode: value.postalCode,
            country: value.countryCode
        };
        this.setState({ campus: campus });
    }

    closeWindow = (returnValue?: string) => {
        window.parent.postMessage(JSON.stringify({ action: "DONE", returnValue: returnValue }), "*");
    }

    cancelWindow = () => {
        window.parent.postMessage(JSON.stringify({ action: "CANCEL"}), "*");
    }

    render() {
        if (!this.state.initialized)
            return <div className="page CampusPage loading"><div className="spinner"></div></div>;

        if (this.state.showAssociateDlg)
            return <div className="page CampusPage">
                <header>
                    <p>Select the existing CertMaster Insitution to associate with Dynamics <span className="bold">{this.props.entityType}</span>.
                        Click <span className="italic">Associate with new CertMaster Institution</span> below if you need to create and associate a new one.
                        </p>
                    {this.state.errorMessage &&
                        <p className="error">{this.state.errorMessage}</p>}
                </header>
                <main>
                   <section className="common">
                        <LabelCtl label="CertMaster Institution">
                            <LookupCtl className="associate" placeholder="Search CertMaster Institutions" disabled={false} value={this.state.associateCampusProfileId} onSearch={this.onAssociateCampusSearch}
                                onChange={this.onAssociateCampusChange} alwaysOpen={true} hideItemValue={true} initialSearchValue={this.state.campus.campusName} />
                       </LabelCtl>
                   </section>
                </main>

                <footer>
                    <button className="tertiary" onClick={() => this.initCampus()}>Associate with new CertMaster Institution</button>
                    <button className="secondary" onClick={() => this.cancelWindow()}>Cancel</button>
                    <button className="primary" onClick={() => this.linkCampus()}>Associate</button>
                </footer>

                {this.state.inProgress &&
                    <div className="saving"><div><div>{this.state.inProgress}</div><div className="spinner"></div></div></div>}
            </div>;

        let { campus } = this.state;
        const postal = new PostalAddress({ line1: campus.address, city: campus.city, stateOrProvince: campus.region, postalCode: campus.postalCode, countryCode: campus.country ?? "USA" });
        return <div className="page CampusPage">
           <header>
               <div className="title">
                    <h3>{!campus.campusId ? " -- New Institution -- " : campus.campusName}</h3>
                    <h6>CertMaster Institution</h6>
               </div>
               <div className="actions">
                </div>
               {this.state.errorMessage &&
                    <p className="error">{this.state.errorMessage}</p>}
           </header>

           <main>
               <section className="full">
                    <LabelCtl label="Name">
                        <TextCtl value={campus.campusName} disabled={false} onChange={this.onNameChange} />
                   </LabelCtl>
                    <LabelCtl label="Display Style">
                        <SelectCtl value={campus.campusDisplayStyle} disabled={false} items={this._displayStyle} hideItemValue={true} onChange={this.onDisplayStyleChange} />
                    </LabelCtl>
                    <LabelCtl label="Address" useDiv={true}>
                        <PostalAddressCtl disabled={false} postal={postal} onChange={this.onPostalChange} defaultCountryCode="USA" hideExtraLines={true} />
                    </LabelCtl>
               </section>
           </main>

            <footer>
                <button className="secondary" onClick={() => this.cancelWindow()}>{this.props.entityId ? "Cancel" : "Close"}</button>
                <button className="primary" disabled={!!this.state.inProgress} onClick={() => this.saveCampus()}>Save{this.props.entityId ? " and Associate" : ""}</button>
           </footer>

            {this.state.inProgress &&
                <div className="saving"><div><div>{this.state.inProgress}</div><div className="spinner"></div></div></div>}
        </div>;
    }
}
